import { useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, ChevronDown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { LanguageContext, LANGS } from "@/i18n";
import { BLOG_UI } from "@/blog/posts";

const scrollToEl = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const { lang, setLang, t } = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const ui = BLOG_UI[lang] || BLOG_UI.en;
  const onHome = location.pathname === `/${lang}`;

  const goSection = (id) => {
    if (!onHome) {
      navigate(`/${lang}`);
      setTimeout(() => scrollToEl(id), 450);
    } else {
      scrollToEl(id);
    }
  };

  const goHome = () => {
    if (!onHome) navigate(`/${lang}`);
    else if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.4 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const current = LANGS.find((l) => l.code === lang) || LANGS[1];
  const links = [
    { id: "vision", label: t.nav.vision, testid: "nav-link-vision" },
    { id: "services", label: t.nav.services, testid: "nav-link-services" },
    { id: "blog", label: ui.menu, testid: "nav-link-blog" },
    { id: "contact", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  const onLink = (l) => {
    if (l.id === "blog") navigate(`/${lang}/blog`);
    else goSection(l.id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled ? "bg-[#0A0A0A]/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={goHome}
          className="flex items-center gap-3 group"
        >
          <span className="w-2.5 h-2.5 bg-[#00F0FF] rotate-45 group-hover:rotate-[135deg] transition-transform duration-500" />
          <span className="font-display font-black text-xl tracking-[0.18em] text-white">DADABIO</span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => onLink(l)}
              className="text-[13px] font-medium tracking-[0.14em] uppercase text-zinc-400 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="relative" ref={dropRef}>
            <button
              data-testid="lang-switcher-button"
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/10 bg-white/[0.03] hover:border-[#00F0FF]/50 hover:bg-[#00F0FF]/5 transition-colors duration-300"
            >
              <Globe size={14} className="text-[#00F0FF]" />
              <span className="text-[13px] font-medium text-zinc-200">{current.label}</span>
              <ChevronDown size={13} className={`text-zinc-500 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {open && (
                <motion.ul
                  data-testid="lang-dropdown-menu"
                  initial={{ opacity: 0, y: -8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.97 }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute end-0 mt-3 w-52 rounded-xl border border-white/10 bg-[#121212]/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-y-auto max-h-[70vh] p-1.5"
                >
                  {LANGS.map((l) => (
                    <li key={l.code}>
                      <button
                        data-testid={`lang-option-${l.code}`}
                        onClick={() => { setLang(l.code); setOpen(false); }}
                        className={`w-full text-start px-3.5 py-2.5 rounded-lg text-sm transition-colors duration-200 flex items-center justify-between ${
                          l.code === lang ? "text-[#00F0FF] bg-[#00F0FF]/10" : "text-zinc-300 hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        {l.label}
                        {l.code === lang && <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF]" />}
                      </button>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          <button
            data-testid="nav-cta-button"
            onClick={() => goSection("contact")}
            className="hidden sm:block px-5 py-2.5 rounded-full bg-white text-black text-[13px] font-semibold tracking-wide hover:bg-[#00F0FF] hover:shadow-[0_0_30px_rgba(0,240,255,0.35)] transition-[background-color,box-shadow] duration-300"
          >
            {t.nav.cta}
          </button>
        </div>
      </div>
    </motion.header>
  );
}
